import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  MoreVertical,
  Plus,
  ClipboardList,
  FolderKanban,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import type {
  CalendarItem,
  CalendarAppointment,
  CalendarViewType,
} from '@/types/agenda'
import {
  appointmentStatusLabels,
  appointmentStatusColors,
  weekdayShortLabels,
} from '@/types/agenda'

interface CalendarViewProps {
  items: CalendarItem[]
  view: CalendarViewType
  onViewChange: (view: CalendarViewType) => void
  onDateChange: (date: Date) => void
  currentDate: Date
  onItemClick?: (item: CalendarItem) => void
  onSlotClick?: (date: Date, hour: number) => void
  isLoading?: boolean
}

// Heures de travail (8h - 20h)
const WORK_HOURS = Array.from({ length: 13 }, (_, i) => i + 8)

/** Retourne la date locale au format YYYY-MM-DD (évite le décalage UTC de toISOString) */
function toLocalDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function CalendarView({
  items,
  view,
  onViewChange,
  onDateChange,
  currentDate,
  onItemClick,
  onSlotClick,
  isLoading,
}: CalendarViewProps) {
  const [hoveredSlot, setHoveredSlot] = useState<string | null>(null)

  // Calculer les dates de la semaine ou du mois
  const { weekDays, monthDays, weekStart } = useMemo(() => {
    // Début de la semaine (Lundi)
    const weekStart = new Date(currentDate)
    const dayOfWeek = weekStart.getDay()
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
    weekStart.setDate(weekStart.getDate() + diff)
    weekStart.setHours(0, 0, 0, 0)

    // Jours de la semaine
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(weekStart)
      date.setDate(date.getDate() + i)
      return date
    })

    // Début du mois
    const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)

    // Jours du mois (avec padding pour commencer au lundi)
    const firstDayOfMonth = monthStart.getDay()
    const startOffset = firstDayOfMonth === 0 ? -6 : 1 - firstDayOfMonth
    const monthGridStart = new Date(monthStart)
    monthGridStart.setDate(monthGridStart.getDate() + startOffset)

    // 6 semaines * 7 jours
    const monthDays = Array.from({ length: 42 }, (_, i) => {
      const date = new Date(monthGridStart)
      date.setDate(date.getDate() + i)
      return date
    })

    return { weekDays, monthDays, weekStart }
  }, [currentDate])

  // Grouper les items par jour
  const itemsByDay = useMemo(() => {
    const map = new Map<string, CalendarItem[]>()
    items.forEach((item) => {
      const dateKey = toLocalDateKey(new Date(item.start_datetime))
      const existing = map.get(dateKey) || []
      map.set(dateKey, [...existing, item])
    })
    return map
  }, [items])

  // Navigation
  const navigatePrev = () => {
    const newDate = new Date(currentDate)
    if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7)
    } else {
      newDate.setMonth(newDate.getMonth() - 1)
    }
    onDateChange(newDate)
  }

  const navigateNext = () => {
    const newDate = new Date(currentDate)
    if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7)
    } else {
      newDate.setMonth(newDate.getMonth() + 1)
    }
    onDateChange(newDate)
  }

  const goToToday = () => {
    onDateChange(new Date())
  }

  // Format du titre
  const getTitle = () => {
    if (view === 'week') {
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekEnd.getDate() + 6)

      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${weekStart.getDate()} - ${weekEnd.getDate()} ${weekEnd.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
      } else {
        return `${weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${weekEnd.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`
      }
    } else {
      return currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    }
  }

  // Vérifier si une date est aujourd'hui
  const isToday = (date: Date) => {
    const today = new Date()
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    )
  }

  // Vérifier si une date est dans le mois courant
  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth()
  }

  return (
    <div className="bg-white rounded-xl shadow-soft overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-beige-200 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={navigatePrev}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={navigateNext}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          <h2 className="text-lg font-semibold text-brown-900 capitalize">
            {getTitle()}
          </h2>

          <Button variant="outline" size="sm" onClick={goToToday}>
            Aujourd'hui
          </Button>
        </div>

        <div className="flex items-center gap-2 bg-beige-100 rounded-lg p-1">
          <button
            onClick={() => onViewChange('week')}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              view === 'week'
                ? 'bg-white text-brown-900 shadow-sm'
                : 'text-brown-600 hover:text-brown-900'
            )}
          >
            Semaine
          </button>
          <button
            onClick={() => onViewChange('month')}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              view === 'month'
                ? 'bg-white text-brown-900 shadow-sm'
                : 'text-brown-600 hover:text-brown-900'
            )}
          >
            Mois
          </button>
        </div>
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-mint-600" />
        </div>
      )}

      {/* Week View */}
      {view === 'week' && (
        <div className="overflow-auto max-h-[calc(100vh-280px)]">
          <div className="min-w-[800px]">
            {/* Header des jours */}
            <div className="grid grid-cols-8 border-b border-beige-200 sticky top-0 bg-white z-10">
              <div className="w-16 border-r border-beige-200" />
              {weekDays.map((day, i) => (
                <div
                  key={i}
                  className={cn(
                    'p-3 text-center border-r border-beige-200 last:border-r-0',
                    isToday(day) && 'bg-mint-50'
                  )}
                >
                  <div className="text-xs text-brown-500 uppercase">
                    {weekdayShortLabels[(i + 1) % 7]}
                  </div>
                  <div
                    className={cn(
                      'text-lg font-semibold mt-1',
                      isToday(day)
                        ? 'w-8 h-8 rounded-full bg-mint-600 text-white flex items-center justify-center mx-auto'
                        : 'text-brown-900'
                    )}
                  >
                    {day.getDate()}
                  </div>
                </div>
              ))}
            </div>

            {/* Grille horaire */}
            {WORK_HOURS.map((hour) => (
              <div key={hour} className="grid grid-cols-8 border-b border-beige-100">
                {/* Heure */}
                <div className="w-16 p-2 text-right text-xs text-brown-400 border-r border-beige-200">
                  {hour}:00
                </div>

                {/* Cellules pour chaque jour */}
                {weekDays.map((day, dayIndex) => {
                  const dateKey = toLocalDateKey(day)
                  const slotKey = `${dateKey}-${hour}`
                  const dayItems = itemsByDay.get(dateKey) || []
                  const hourItems = dayItems.filter((item) => {
                    const itemHour = new Date(item.start_datetime).getHours()
                    return itemHour === hour
                  })

                  return (
                    <div
                      key={dayIndex}
                      className={cn(
                        'min-h-[60px] border-r border-beige-100 last:border-r-0 relative',
                        isToday(day) && 'bg-mint-50/50',
                        hoveredSlot === slotKey && 'bg-beige-50'
                      )}
                      onMouseEnter={() => setHoveredSlot(slotKey)}
                      onMouseLeave={() => setHoveredSlot(null)}
                      onClick={() => onSlotClick?.(day, hour)}
                    >
                      {/* Bouton d'ajout au survol */}
                      <AnimatePresence>
                        {hoveredSlot === slotKey && hourItems.length === 0 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 flex items-center justify-center cursor-pointer"
                          >
                            <div className="w-6 h-6 rounded-full bg-mint-100 flex items-center justify-center">
                              <Plus className="w-4 h-4 text-mint-600" />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Items (RDV + Tâches) */}
                      {hourItems.map((item) => (
                        <CalendarItemCard
                          key={item.id}
                          item={item}
                          onClick={() => onItemClick?.(item)}
                          compact
                        />
                      ))}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Month View */}
      {view === 'month' && (
        <div>
          {/* Header des jours */}
          <div className="grid grid-cols-7 border-b border-beige-200">
            {[1, 2, 3, 4, 5, 6, 0].map((day) => (
              <div
                key={day}
                className="p-2 text-center text-xs font-medium text-brown-500 uppercase"
              >
                {weekdayShortLabels[day]}
              </div>
            ))}
          </div>

          {/* Grille des jours */}
          <div className="grid grid-cols-7">
            {monthDays.map((day, index) => {
              const dateKey = toLocalDateKey(day)
              const dayItems = itemsByDay.get(dateKey) || []
              const isInMonth = isCurrentMonth(day)

              return (
                <div
                  key={index}
                  className={cn(
                    'min-h-[100px] p-2 border-b border-r border-beige-100',
                    !isInMonth && 'bg-beige-50/50',
                    isToday(day) && 'bg-mint-50',
                    (index + 1) % 7 === 0 && 'border-r-0'
                  )}
                  onClick={() => onSlotClick?.(day, 9)}
                >
                  <div
                    className={cn(
                      'text-sm font-medium mb-1',
                      isToday(day)
                        ? 'w-6 h-6 rounded-full bg-mint-600 text-white flex items-center justify-center'
                        : isInMonth
                        ? 'text-brown-900'
                        : 'text-brown-300'
                    )}
                  >
                    {day.getDate()}
                  </div>

                  {/* Liste des items */}
                  <div className="space-y-1">
                    {dayItems.slice(0, 3).map((item) => (
                      <MonthItemPill
                        key={item.id}
                        item={item}
                        onClick={(e) => {
                          e.stopPropagation()
                          onItemClick?.(item)
                        }}
                      />
                    ))}
                    {dayItems.length > 3 && (
                      <div className="text-xs text-brown-500">
                        +{dayItems.length - 3} autres
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// Month view pill for items
function MonthItemPill({ item, onClick }: { item: CalendarItem; onClick: (e: React.MouseEvent) => void }) {
  if (item.type === 'task') {
    return (
      <div
        className="text-xs p-1 rounded truncate cursor-pointer bg-violet-100 text-violet-800"
        onClick={onClick}
      >
        <ClipboardList className="w-3 h-3 inline mr-1" />
        {item.title}
      </div>
    )
  }

  // appointment
  const apt = item as CalendarAppointment
  return (
    <div
      className={cn(
        'text-xs p-1 rounded truncate cursor-pointer',
        apt.status === 'confirmed'
          ? 'bg-mint-100 text-mint-800'
          : apt.status === 'pending'
          ? 'bg-gold-100 text-gold-800'
          : apt.status === 'cancelled'
          ? 'bg-beige-100 text-brown-400 line-through'
          : 'bg-beige-100 text-brown-600'
      )}
      onClick={onClick}
    >
      {new Date(apt.start_datetime).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      })}{' '}
      {apt.client_name}
    </div>
  )
}

// Composant carte de CalendarItem (week view)
interface CalendarItemCardProps {
  item: CalendarItem
  onClick?: () => void
  compact?: boolean
}

function CalendarItemCard({ item, onClick, compact }: CalendarItemCardProps) {
  const startTime = new Date(item.start_datetime).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })

  if (item.type === 'task') {
    // Task display
    if (compact) {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute inset-x-1 top-1 p-1.5 rounded text-xs cursor-pointer border-l-2 shadow-sm bg-violet-50 border-violet-500"
          onClick={(e) => {
            e.stopPropagation()
            onClick?.()
          }}
        >
          <div className="font-medium truncate text-violet-900 flex items-center gap-1">
            <ClipboardList className="w-3 h-3 flex-shrink-0" />
            {item.title}
          </div>
          <div className="truncate text-violet-600">{item.project_name}</div>
        </motion.div>
      )
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-3 bg-white rounded-lg border border-violet-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
        onClick={onClick}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-3.5 h-3.5 text-violet-500" />
              <span className="text-sm font-medium text-brown-900">{item.title}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <FolderKanban className="w-3.5 h-3.5 text-brown-400" />
              <span className="text-sm text-brown-700 truncate">{item.project_name}</span>
            </div>
          </div>
        </div>
      </motion.div>
    )
  }

  // Appointment display
  const apt = item as CalendarAppointment
  const statusConfig = appointmentStatusColors[apt.status]

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          'absolute inset-x-1 top-1 p-1.5 rounded text-xs cursor-pointer',
          'border-l-2 shadow-sm',
          apt.status === 'confirmed'
            ? 'bg-mint-50 border-mint-500'
            : apt.status === 'pending'
            ? 'bg-gold-50 border-gold-500'
            : apt.status === 'cancelled'
            ? 'bg-beige-50 border-brown-300 opacity-50'
            : 'bg-beige-50 border-brown-400'
        )}
        onClick={(e) => {
          e.stopPropagation()
          onClick?.()
        }}
      >
        <div className="font-medium truncate text-brown-900">
          {startTime} - {apt.client_name}
        </div>
        <div className="truncate text-brown-500">{apt.subject}</div>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-3 bg-white rounded-lg border border-beige-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-brown-400" />
            <span className="text-sm font-medium text-brown-900">{startTime}</span>
            <Badge variant={statusConfig.variant} size="sm">
              {appointmentStatusLabels[apt.status]}
            </Badge>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <User className="w-3.5 h-3.5 text-brown-400" />
            <span className="text-sm text-brown-700 truncate">
              {apt.client_name}
            </span>
          </div>
          <div className="mt-1 text-sm text-brown-500 truncate">
            {apt.subject}
          </div>
        </div>
        <button className="p-1 hover:bg-beige-100 rounded">
          <MoreVertical className="w-4 h-4 text-brown-400" />
        </button>
      </div>
    </motion.div>
  )
}

export default CalendarView
